import { useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';

export type UnitType = 'education_consultant' | 'visa_consultant' | 'marketing' | 'accounting' | 'customer_service' | 'sales';

export interface Unit {
  id: string;
  name: string;
  type: UnitType;
  branch_id: string;
  manager: string;
  email: string;
  phone: string;
  description?: string;
  location?: string;
  working_hours: string;
  capacity?: number;
  monthly_target: number;
  specializations: string[];
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreateUnitInput {
  name: string;
  type: UnitType;
  branch_id: string;
  manager: string;
  email: string;
  phone: string;
  description?: string;
  location?: string;
  working_hours?: string;
  capacity?: number;
  monthly_target?: number;
  specializations?: string[];
  is_active?: boolean;
}

export const useUnits = () => {
  const [units, setUnits] = useState<Unit[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchUnits = useCallback(async (branchId?: string) => {
    setLoading(true);
    setError(null);

    try {
      let query = supabase
        .from('units')
        .select('*')
        .order('created_at', { ascending: false });

      if (branchId) {
        query = query.eq('branch_id', branchId);
      }

      const { data, error: fetchError } = await query;

      if (fetchError) throw fetchError;

      setUnits(data || []);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Birimler yüklenirken hata oluştu';
      setError(errorMessage);
      console.error('Error fetching units:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const createUnit = useCallback(async (unitData: CreateUnitInput) => {
    setLoading(true);
    setError(null);

    try {
      const { data, error: insertError } = await supabase
        .from('units')
        .insert([{
          name: unitData.name,
          type: unitData.type,
          branch_id: unitData.branch_id,
          manager: unitData.manager,
          email: unitData.email,
          phone: unitData.phone,
          description: unitData.description || '',
          location: unitData.location || '',
          working_hours: unitData.working_hours || '09:00 - 18:00',
          capacity: unitData.capacity || null,
          monthly_target: unitData.monthly_target || 0,
          specializations: unitData.specializations || [],
          is_active: unitData.is_active !== undefined ? unitData.is_active : true
        }])
        .select()
        .single();

      if (insertError) throw insertError;

      await fetchUnits();

      return { data, error: null };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Birim oluşturulurken hata oluştu';
      setError(errorMessage);
      console.error('Error creating unit:', err);
      return { data: null, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, [fetchUnits]);

  const updateUnit = useCallback(async (id: string, updates: Partial<CreateUnitInput>) => {
    setLoading(true);
    setError(null);

    try {
      const { data, error: updateError } = await supabase
        .from('units')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .select()
        .single();

      if (updateError) throw updateError;

      await fetchUnits();

      return { data, error: null };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Birim güncellenirken hata oluştu';
      setError(errorMessage);
      console.error('Error updating unit:', err);
      return { data: null, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, [fetchUnits]);

  const deleteUnit = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);

    try {
      const { error: deleteError } = await supabase
        .from('units')
        .delete()
        .eq('id', id);

      if (deleteError) throw deleteError;

      await fetchUnits();

      return { error: null };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Birim silinirken hata oluştu';
      setError(errorMessage);
      console.error('Error deleting unit:', err);
      return { error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, [fetchUnits]);

  return {
    units,
    loading,
    error,
    fetchUnits,
    createUnit,
    updateUnit,
    deleteUnit
  };
};
